<template>
  <div class="learn-more-page">
    <!-- Hero intro -->
    <section class="lm-hero">
      <div class="lm-hero__content">
        <h1 class="lm-hero__title">The Magic Behind the Mail</h1>
        <p class="lm-hero__subtitle">
          Thinking of U helps you send love, laughs, and thoughtful surprises through
          beautifully crafted postcards — generated just for you.
        </p>
      </div>
    </section>

    <!-- Scroll-driven postcard journey -->
    <ScrollSequence />

    <!-- Mission -->
    <section class="lm-section lm-section--pink">
      <div class="lm-container reveal-item">
        <h2 class="lm-heading">Our Mission</h2>
        <p class="lm-text">
          We believe in the magic of surprise and the power of tiny gestures. Whether it's a
          romantic note, a joke for a friend, or an "I miss you" moment — Thinking of U makes
          it easy to say something that matters, without overthinking it.
        </p>
      </div>
    </section>

    <!-- How It Works -->
    <section class="lm-section lm-section--white">
      <div class="lm-container">
        <h2 class="lm-heading reveal-item">How It Works</h2>
        <div class="lm-steps">
          <div class="lm-step reveal-item" style="transition-delay: 0.1s">
            <div class="lm-step__number">1</div>
            <h3>Choose the vibe</h3>
            <p>Pick your tone, recipient, and style. Set an occasion if you'd like.</p>
          </div>
          <div class="lm-step reveal-item" style="transition-delay: 0.2s">
            <div class="lm-step__number">2</div>
            <h3>Let go and trust the magic</h3>
            <p>We generate a one-of-a-kind message and image. No edits. No previews.</p>
          </div>
          <div class="lm-step reveal-item" style="transition-delay: 0.3s">
            <div class="lm-step__number">3</div>
            <h3>Send and smile</h3>
            <p>We print and mail your card, delivering a little burst of joy to someone's mailbox.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us -->
    <section class="lm-section lm-section--white">
      <div class="lm-container">
        <h2 class="lm-heading reveal-item">Why Thinking of U?</h2>
        <div class="lm-features">
          <div class="lm-feature reveal-item" style="transition-delay: 0.1s">
            <h3>Surprise-first design</h3>
            <p>No previews or edits — just intention, trust, and a little chaos.</p>
          </div>
          <div class="lm-feature reveal-item" style="transition-delay: 0.2s">
            <h3>Genuinely unique</h3>
            <p>Every card is generated from scratch and feels one-of-a-kind — because it is.</p>
          </div>
          <div class="lm-feature reveal-item" style="transition-delay: 0.3s">
            <h3>Simple, fun process</h3>
            <p>It takes only a minute to create something meaningful.</p>
          </div>
          <div class="lm-feature reveal-item" style="transition-delay: 0.4s">
            <h3>More than mail</h3>
            <p>It's a vibe. A small act of care in a noisy world.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="lm-section lm-section--pink">
      <div class="lm-container reveal-item">
        <h2 class="lm-heading">What People Are Saying</h2>
        <TestimonialCarousel />
      </div>
    </section>

    <!-- CTA -->
    <section class="lm-section lm-section--cta">
      <div class="lm-container reveal-item">
        <h2 class="lm-heading">Ready to Get Started?</h2>
        <p class="lm-text">
          Start your first surprise today — it's weird in the best way, and more heartfelt
          than you'd think.
        </p>
        <button @click="goToCreatePage" class="cta-button cta-button--large">
          Create Your Postcard
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import TestimonialCarousel from '@/components/TestimonialCarousel.vue';
import ScrollSequence from '@/components/ScrollSequence.vue';

export default {
  name: 'LearnMore',
  components: { TestimonialCarousel, ScrollSequence },
  data() {
    return {
      observer: null,
    };
  },
  mounted() {
    this.initScrollReveal();
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    goToCreatePage() {
      this.$router.push('/create-postcard');
    },
    initScrollReveal() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('revealed');
              this.observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
      );

      this.$nextTick(() => {
        const items = this.$el.querySelectorAll('.reveal-item');
        items.forEach((el) => this.observer.observe(el));
      });
    },
  },
};
</script>

<style scoped>
.learn-more-page {
  background-color: var(--light-pink);
  overflow-x: hidden;
}

/* === Scroll reveal === */
.reveal-item {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.reveal-item.revealed {
  opacity: 1;
  transform: translateY(0);
}

/* === Hero === */
.lm-hero {
  padding: 5rem 2rem 3rem;
  text-align: center;
  background: linear-gradient(180deg, var(--light-pink) 0%, white 100%);
}

.lm-hero__title {
  font-size: 2.8rem;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
}

.lm-hero__subtitle {
  font-size: 1.15rem;
  color: #555;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
}

/* === Sections === */
.lm-section {
  padding: 4rem 2rem;
}

.lm-section--pink {
  background-color: var(--light-pink);
}

.lm-section--white {
  background-color: white;
}

.lm-section--dark {
  background: linear-gradient(135deg, var(--rich-black), #1a1a2e);
}

.lm-section--cta {
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  text-align: center;
}

.lm-container {
  max-width: 900px;
  margin: 0 auto;
}

/* === Typography === */
.lm-heading {
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
  display: inline-block;
  width: 100%;
}

.lm-heading::after {
  content: '';
  display: block;
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, var(--hollywood-cerise), var(--violet-lush));
  border-radius: 2px;
  margin: 0.5rem auto 0;
}

.lm-heading--light {
  color: white;
}

.lm-heading--light::after {
  background: linear-gradient(90deg, rgba(255,255,255,0.6), rgba(255,255,255,0.2));
}

.lm-text {
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--rich-black);
  text-align: center;
}

.lm-text--light {
  color: rgba(255, 255, 255, 0.85);
}

/* === Steps === */
.lm-steps {
  display: flex;
  gap: 2rem;
  margin-top: 1rem;
}

.lm-step {
  flex: 1;
  text-align: center;
  padding: 1.5rem;
}

.lm-step__number {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  margin: 0 auto 1rem;
}

.lm-step h3 {
  color: var(--violet-lush);
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}

.lm-step p {
  color: #555;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* === Callout === */
.lm-callout {
  text-align: center;
  padding: 1rem 0;
}

.lm-link {
  display: inline-block;
  margin-top: 1rem;
  color: var(--hollywood-cerise);
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s;
}

.lm-link:hover {
  color: white;
}

/* === Features grid === */
.lm-features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 1rem;
}

.lm-feature {
  background: var(--light-pink);
  padding: 1.5rem;
  border-radius: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.lm-feature:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.lm-feature h3 {
  color: var(--hollywood-cerise);
  font-size: 1.05rem;
  margin-bottom: 0.5rem;
}

.lm-feature p {
  color: #555;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
}

/* === CTA section === */
.lm-section--cta .lm-heading {
  color: white;
}

.lm-section--cta .lm-heading::after {
  background: rgba(255, 255, 255, 0.4);
}

.lm-section--cta .lm-text {
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
}

.cta-button--large {
  padding: 16px 40px;
  font-size: 1.15rem;
  font-weight: 600;
  background: white;
  color: var(--hollywood-cerise);
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.cta-button--large:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  background: white;
  color: var(--violet-lush);
}

/* === Mobile === */
@media (max-width: 768px) {
  .lm-hero__title {
    font-size: 2rem;
  }

  .lm-steps {
    flex-direction: column;
  }

  .lm-features {
    grid-template-columns: 1fr;
  }
}

/* === Reduced motion === */
@media (prefers-reduced-motion: reduce) {
  .reveal-item {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
